import React,{useEffect,useMemo,useRef,useState} from 'react';
import { Database, Edit3, KeyRound, Link2, Plus, RefreshCw, ShieldCheck, Trash2, X, Layers3, Workflow, FileSpreadsheet, ChevronDown } from 'lucide-react';

const TYPES={
  'jira-rest':{label:'Jira REST',url:'https://your-domain.atlassian.net',auth:'api_token',userLabel:'Email / account',secretLabel:'API token'},
  'servicenow-rest':{label:'ServiceNow REST',url:'https://your-instance.service-now.com',auth:'basic',userLabel:'Username',secretLabel:'Password / token'},
};
const JIRA_METHODS=[
  {id:'oauth',label:'Connect Atlassian',hint:'Recommended',description:'OAuth 2.0. Users approve access in Atlassian; Process Advisor stores rotating tokens server-side.'},
  {id:'api_token',label:'API token',hint:'Simple',description:'Classic Atlassian account token using your Jira site URL and account email.'},
  {id:'scoped_api_token',label:'Scoped API token',hint:'Least privilege',description:'Scoped Atlassian token. Process Advisor discovers the Cloud ID and calls the Atlassian API gateway.'},
];
const SERVICENOW_METHODS=[
  {id:'oauth',label:'Connect ServiceNow',hint:'Recommended',description:'OAuth 2.0 authorization-code flow. A ServiceNow admin registers Process Advisor as an OAuth client in the customer instance.'},
  {id:'client_credentials',label:'Client credentials',hint:'Enterprise',description:'OAuth 2.0 machine-to-machine access using a Client ID and Client Secret. No interactive user approval is required for each run.'},
  {id:'basic',label:'Integration user',hint:'Simple',description:'Dedicated ServiceNow integration account using username and password. Supported for customers that still allow Basic Authentication.'},
  {id:'bearer',label:'Bearer token',hint:'Advanced',description:'Use an existing OAuth access token. Best for testing or externally managed token lifecycles.'},
];
const blank={name:'',type:'jira-rest',baseUrl:'',authType:'api_token',jiraAuthMethod:'oauth',serviceNowAuthMethod:'oauth',username:'',secret:''};

const methodLabel=item=>{
  if(item.type==='jira-rest'){
    const method=item.jiraAuthMethod||item.config?.jiraAuthMethod||'api_token';
    return method==='oauth'?'OAuth 2.0':method==='scoped_api_token'?'Scoped token':'API token';
  }
  if(item.type==='servicenow-rest'){
    const method=item.serviceNowAuthMethod||item.config?.serviceNowAuthMethod||item.authType||'basic';
    return method==='oauth'?'OAuth 2.0':method==='client_credentials'?'Client credentials':method==='bearer'?'Bearer token':'Integration user';
  }
  return '';
};

export default function ConnectionsPanel({repository,onChanged,onAddCsv}){
  const [items,setItems]=useState([]);
  const [editing,setEditing]=useState(null);
  const [showSourcePicker,setShowSourcePicker]=useState(false);
  const [form,setForm]=useState(blank);
  const [busy,setBusy]=useState('');
  const [message,setMessage]=useState('');
  const [editorTest,setEditorTest]=useState({state:'idle',message:''});
  const oauthPopupRef=useRef(null);
  const oauthPollRef=useRef(null);
  const clearOauthWatcher=()=>{
    if(oauthPollRef.current!==null){window.clearInterval(oauthPollRef.current);oauthPollRef.current=null;}
    oauthPopupRef.current=null;
  };
  // A popup closed by the user sends no OAuth callback message.
  useEffect(()=>()=>{
    if(oauthPollRef.current!==null)window.clearInterval(oauthPollRef.current);
  },[]);

  const load=async()=>{const next=await repository.listSourceConnections();setItems(next||[]);onChanged?.(next||[]);return next||[];};
  useEffect(()=>{load().catch(e=>setMessage(e.message));},[]);

  useEffect(()=>{
    const handler=async event=>{
      if(event.origin!==window.location.origin)return;
      if(event.data?.type==='process-advisor-atlassian-oauth'){
        clearOauthWatcher();
        if(event.data.ok){
          setEditorTest({state:'success',message:'Atlassian connected successfully.'});
          await load().catch(()=>{});
          setMessage('Atlassian OAuth connection saved.');
          setEditing(null);setForm(blank);
        }else setEditorTest({state:'error',message:event.data?.error||'Atlassian connection failed'});
        setBusy('');
        return;
      }
      if(event.data?.type==='process-advisor-servicenow-oauth'){
        if(event.data.ok){
          setEditorTest({state:'success',message:'ServiceNow connected successfully.'});
          await load().catch(()=>{});
          setMessage('ServiceNow OAuth connection saved.');
          setEditing(null);setForm(blank);
        }else setEditorTest({state:'error',message:event.data?.error||'ServiceNow connection failed'});
        setBusy('');
      }
    };
    window.addEventListener('message',handler);
    return()=>window.removeEventListener('message',handler);
  },[]);

  const typeInfo=TYPES[form.type]||TYPES['jira-rest'];
  const beginNew=type=>{
    const info=TYPES[type]||TYPES['jira-rest'];
    setShowSourcePicker(false);
    setEditing('new');
    setForm({...blank,type,authType:info.auth,jiraAuthMethod:type==='jira-rest'?'oauth':'',serviceNowAuthMethod:type==='servicenow-rest'?'oauth':''});
    setMessage('');setEditorTest({state:'idle',message:''});
  };
  const beginEdit=item=>{
    setEditing(item.id);
    setForm({
      name:item.name,type:item.type,baseUrl:item.baseUrl,
      authType:item.authType||TYPES[item.type]?.auth||'api_token',
      jiraAuthMethod:item.jiraAuthMethod||item.config?.jiraAuthMethod||(item.authType==='oauth'?'oauth':item.authType==='bearer'?'scoped_api_token':'api_token'),
      serviceNowAuthMethod:item.serviceNowAuthMethod||item.config?.serviceNowAuthMethod||(item.authType==='client_credentials'?'client_credentials':item.authType==='oauth'?'oauth':item.authType==='bearer'?'bearer':'basic'),
      username:item.username||'',secret:'',
    });
    setMessage('');setEditorTest({state:'idle',message:''});
  };
  const close=()=>{
    if(oauthPopupRef.current && !oauthPopupRef.current.closed)oauthPopupRef.current.close();
    clearOauthWatcher();
    setBusy('');setEditing(null);setForm(blank);setEditorTest({state:'idle',message:''});
  };

  const connectionPayload=()=>({
    ...form,
    authType:form.type==='jira-rest'
      ? (form.jiraAuthMethod==='scoped_api_token'?'bearer':form.jiraAuthMethod==='oauth'?'oauth':'api_token')
      : (form.serviceNowAuthMethod||'basic'),
    config:form.type==='jira-rest'
      ? {jiraAuthMethod:form.jiraAuthMethod}
      : {serviceNowAuthMethod:form.serviceNowAuthMethod||'basic'},
    serviceNowAuthMethod:form.type==='servicenow-rest'?form.serviceNowAuthMethod:undefined,
    secret:form.secret||undefined,
  });

  const save=async()=>{
    setBusy('save');setMessage('');
    try{
      const payload=connectionPayload();
      const saved=editing==='new'
        ? await repository.createSourceConnection(payload)
        : await repository.updateSourceConnection(editing,payload);
      // Validate exactly what the API persisted, not the unsaved editor credential.
      if((form.type==='jira-rest' && form.jiraAuthMethod!=='oauth') && saved?.id){
        try{
          const verified=await repository.testSourceConnection(saved.id);
          await load();close();setMessage(`Connection saved and verified. ${verified.message||''}`);
        }catch(testError){
          await load();close();setMessage(`Connection saved, but the stored credential failed verification: ${testError.message||'Test failed'}. Edit the connection to retry.`);
        }
      }else{
        await load();close();setMessage('Connection saved. You can test it again at any time.');
      }
    }catch(e){setMessage(e.message||'Unable to save connection');}
    finally{setBusy('');}
  };

  const connectAtlassian=()=>{
    if(!form.name.trim()||!form.baseUrl.trim()){
      setEditorTest({state:'error',message:'Enter a connection name and Jira site URL first.'});return;
    }
    setBusy('oauth');setEditorTest({state:'testing',message:'Waiting for Atlassian authorization…'});
    const params=new URLSearchParams({name:form.name.trim(),baseUrl:form.baseUrl.trim()});
    if(editing!=='new')params.set('connectionId',editing);
    const popup=window.open(`/api/source-connections/oauth/atlassian/start?${params.toString()}`,'pa-atlassian-oauth','width=680,height=760,resizable=yes,scrollbars=yes');
    if(!popup){setBusy('');setEditorTest({state:'error',message:'Your browser blocked the Atlassian authorization window.'});return;}
    clearOauthWatcher();
    oauthPopupRef.current=popup;
    oauthPollRef.current=window.setInterval(()=>{
      if(oauthPopupRef.current?.closed){
        clearOauthWatcher();
        setBusy('');
        setEditorTest({state:'idle',message:''});
        setMessage('Atlassian authorisation was cancelled. Your existing connection has not changed.');
      }
    },400);
  };


  const connectServiceNow=async()=>{
    if(!form.name.trim()||!form.baseUrl.trim()||!form.username.trim()){
      setEditorTest({state:'error',message:'Enter a connection name, ServiceNow instance URL and Client ID first.'});return;
    }
    setBusy('servicenow-oauth');
    setEditorTest({state:'testing',message:'Preparing ServiceNow authorization…'});
    try{
      let connectionId=editing!=='new'?editing:null;
      const payload=connectionPayload();
      if(!connectionId){
        if(!form.secret.trim()) throw new Error('Enter the ServiceNow Client Secret first.');
        const saved=await repository.createSourceConnection(payload);
        connectionId=saved.id;
        setEditing(connectionId);
      }else if(form.secret.trim()){
        await repository.updateSourceConnection(connectionId,payload);
      }
      const popup=window.open(`/api/source-connections/oauth/servicenow/start?connectionId=${encodeURIComponent(connectionId)}`,'pa-servicenow-oauth','width=700,height=780,resizable=yes,scrollbars=yes');
      if(!popup) throw new Error('Your browser blocked the ServiceNow authorization window.');
      setEditorTest({state:'testing',message:'Waiting for ServiceNow authorization…'});
    }catch(e){
      setBusy('');
      setEditorTest({state:'error',message:e.message||'Unable to start ServiceNow authorization'});
    }
  };

  const testForm=async()=>{
    if(form.type==='jira-rest'&&form.jiraAuthMethod==='oauth'){
      if(editing==='new')return connectAtlassian();
      setBusy('test-form');setEditorTest({state:'testing',message:'Testing Atlassian OAuth connection…'});
      try{const result=await repository.testSourceConnection(editing);setEditorTest({state:'success',message:result.message||'Connection successful.'});await load();}
      catch(e){setEditorTest({state:'error',message:e.message||'Connection test failed'});}
      finally{setBusy('');}
      return;
    }
    if(form.type==='servicenow-rest'&&form.serviceNowAuthMethod==='oauth'){
      if(editing==='new')return connectServiceNow();
      setBusy('test-form');setEditorTest({state:'testing',message:'Testing ServiceNow OAuth connection…'});
      try{const result=await repository.testSourceConnection(editing);setEditorTest({state:'success',message:result.message||'Connection successful.'});await load();}
      catch(e){setEditorTest({state:'error',message:e.message||'Connection test failed'});}
      finally{setBusy('');}
      return;
    }
    setBusy('test-form');setEditorTest({state:'testing',message:'Testing connection…'});
    try{
      const payload={...(editing!=='new'?{id:editing}:{}),...connectionPayload()};
      const result=(editing!=='new'&&!form.secret.trim())
        ? await repository.testSourceConnection(editing)
        : await repository.testSourceConnectionInput(payload);
      const draft=editing!=='new' && Boolean(form.secret.trim());
      setEditorTest({state:'success',message:draft
        ? 'New token tested successfully. Save to verify the stored connection.'
        : (result.message||'Connection successful.')});
      if(editing!=='new' && !draft)await load();
    }catch(e){
      setEditorTest({state:'error',message:e.message||'Connection test failed'});
      if(editing!=='new')await load().catch(()=>{});
    }finally{setBusy('');}
  };

  const test=async item=>{
    setBusy(`test-${item.id}`);setMessage('');
    try{const result=await repository.testSourceConnection(item.id);await load();setMessage(result.message||'Connection successful.');}
    catch(e){setMessage(e.message||'Connection test failed');}
    finally{setBusy('');}
  };
  const remove=async item=>{
    if(!window.confirm(`Delete connection "${item.name}"? Models using it will need another connection before they can refresh.`))return;
    setBusy(`delete-${item.id}`);
    try{await repository.deleteSourceConnection(item.id);await load();setMessage('Connection deleted.');}
    catch(e){setMessage(e.message||'Unable to delete connection');}
    finally{setBusy('');}
  };
  const counts=useMemo(()=>({jira:items.filter(i=>i.type==='jira-rest').length,snow:items.filter(i=>i.type==='servicenow-rest').length}),[items]);
  const isJira=form.type==='jira-rest';
  const isServiceNow=form.type==='servicenow-rest';
  const jiraMethod=form.jiraAuthMethod||'oauth';
  const serviceNowMethod=form.serviceNowAuthMethod||'oauth';
  const oauthSaved=editing!=='new'&&jiraMethod==='oauth';
  const serviceNowOauthSaved=editing!=='new'&&serviceNowMethod==='oauth';

  return <div className="connections-settings">
    {editing!=='new'&&<div className="connections-toolbar compact">
      <div className="connections-title-block">
        <div className="connections-title-line">
          <h2>Source connections</h2>
          <span className="connections-count">{items.length} total · {counts.jira} Jira · {counts.snow} ServiceNow</span>
        </div>
        <p>Reusable source connections for process models.</p>
      </div>
      <div className="connections-add-area">
        <button type="button" className="pa-primary connections-add-trigger" aria-expanded={showSourcePicker} onClick={()=>setShowSourcePicker(value=>!value)}><Plus size={17}/> Add data source <ChevronDown size={15}/></button>
        {showSourcePicker&&<div className="connections-source-picker">
          <div className="connections-picker-heading">Choose a data source</div>
          <button type="button" onClick={()=>beginNew('jira-rest')}><span className="connection-mark jira-rest"><Layers3 size={19}/></span><span><b>Jira</b><small>Atlassian Cloud</small></span></button>
          <button type="button" onClick={()=>beginNew('servicenow-rest')}><span className="connection-mark servicenow-rest"><Workflow size={19}/></span><span><b>ServiceNow</b><small>ServiceNow instance</small></span></button>
          <button type="button" onClick={()=>{setShowSourcePicker(false);onAddCsv?.();}}><span className="connection-mark csv-file"><FileSpreadsheet size={19}/></span><span><b>CSV file</b><small>Upload and map CSV data</small></span></button>
          <div className="connections-picker-note">Additional data sources can be supported here in future.</div>
        </div>}
      </div>
    </div>}
    {message&&<div className="settings-status compact">{message}</div>}

    {editing!=='new'&&(items.length===0?<div className="empty-connections"><Link2 size={28}/><b>No source connections yet</b><span>Select Add data source to connect a system or import a CSV file. Private credentials are encrypted server-side and are never returned to the browser.</span></div>:
      <div className="connection-table-wrap">
        <div className="connection-table-head" aria-hidden="true"><span>Connection</span><span>Source system</span><span>Connection type</span><span>Site / instance</span><span>Status</span><span>Actions</span></div>
        <div className="connection-list">
          {items.map(item=><div className="connection-row connection-detail-row" key={item.id}>
            <div className="connection-name-cell"><span className={`connection-mark ${item.type}`}>{item.type==='jira-rest'?<Layers3 size={18}/>:item.type==='servicenow-rest'?<Workflow size={18}/>:<Link2 size={18}/>}</span><b title={item.name}>{item.name}</b></div>
            <div className="connection-source-cell"><span className={`connection-source-tag ${item.type}`}>{item.type==='jira-rest'?'Jira':item.type==='servicenow-rest'?'ServiceNow':TYPES[item.type]?.label||item.type}</span></div>
            <div className="connection-auth-cell">{methodLabel(item)||'—'}<small>REST API</small></div>
            <div className="connection-url-cell" title={item.baseUrl||''}>{item.baseUrl||'—'}</div>
            <div><span className={`status-pill ${item.status==='connected'?'success':item.status==='error'?'danger':'neutral'}`}>{item.status==='untested'?'Configured':item.status}</span></div>
            <div className="connection-row-actions">
              <button className="icon-button" aria-label={`Test ${item.name}`} title="Test connection" disabled={busy===`test-${item.id}`} onClick={()=>test(item)}><RefreshCw size={15}/></button>
              <button className="icon-button" aria-label={`Edit ${item.name}`} title="Edit connection" onClick={()=>beginEdit(item)}><Edit3 size={15}/></button>
              <button className="icon-button danger" aria-label={`Delete ${item.name}`} title="Delete connection" onClick={()=>remove(item)}><Trash2 size={15}/></button>
            </div>
          </div>)}
        </div>
      </div>
    )}

    {editing&&<div className="connection-editor compact">
      <div className="connection-editor-head compact">
        <div>
          <div className="connection-editor-title-line">
            <h3>{editing==='new'?'New connection':'Edit connection'}</h3>
            <span>{typeInfo.label}</span>
          </div>
        </div>
        <button className="icon-button" onClick={close}><X size={17}/></button>
      </div>

      <div className="connection-core-grid">
        <label>
          <span>Connection name</span>
          <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder={isJira?'Jira Production':'ServiceNow Production'}/>
        </label>
        <label className="connection-type-field">
          <span>Source</span>
          <select value={form.type} disabled={editing!=='new'} onChange={e=>{const nextType=e.target.value;const info=TYPES[nextType];setForm({...form,type:nextType,authType:info.auth,jiraAuthMethod:nextType==='jira-rest'?'oauth':'',serviceNowAuthMethod:nextType==='servicenow-rest'?'oauth':'',username:'',secret:''});}}>
            <option value="jira-rest">Jira REST</option>
            <option value="servicenow-rest">ServiceNow REST</option>
          </select>
        </label>
        <label className="connection-url-field">
          <span>{isJira?'Jira site URL':'Base URL'}</span>
          <input value={form.baseUrl} onChange={e=>setForm({...form,baseUrl:e.target.value})} placeholder={typeInfo.url}/>
        </label>
      </div>

      {isJira&&<div className="jira-auth-section compact">
        <div className="jira-auth-heading compact">
          <b>Connection method</b>
          <span>OAuth is recommended for customer SaaS connections.</span>
        </div>

        <div className="jira-auth-methods compact">
          {JIRA_METHODS.map(method=><button
            type="button"
            key={method.id}
            className={`jira-auth-method compact ${jiraMethod===method.id?'selected':''}`}
            onClick={()=>setForm({...form,jiraAuthMethod:method.id,secret:'',username:method.id==='oauth'?'':form.username})}
          >
            <span className="jira-auth-radio" aria-hidden="true"/>
            <span className="jira-auth-compact-label">
              <b>{method.label}</b>
              <em>{method.hint}</em>
            </span>
          </button>)}
        </div>

        <div className="jira-auth-selected-help">
          {JIRA_METHODS.find(method=>method.id===jiraMethod)?.description}
        </div>
      </div>}

      {isJira&&jiraMethod==='oauth'&&<div className="oauth-connect-panel compact">
        <div>
          <b>{oauthSaved?'Atlassian OAuth connected':'Connect to Atlassian'}</b>
          <span>{oauthSaved?'Reconnect only if access was revoked or changed.':'Sign in to Atlassian and approve read access. Process Advisor stores the OAuth tokens server-side.'}</span>
        </div>
        <button className="pa-primary" disabled={!!busy||!form.name.trim()||!form.baseUrl.trim()} onClick={connectAtlassian}>
          <Link2 size={15}/>{busy==='oauth'?'Waiting…':oauthSaved?'Reconnect':'Connect Atlassian'}
        </button>
      </div>}

      {isServiceNow&&<div className="jira-auth-section compact">
        <div className="jira-auth-heading compact">
          <b>Connection method</b>
          <span>Use OAuth where possible. Client credentials is ideal for unattended enterprise extraction.</span>
        </div>

        <div className="servicenow-auth-methods compact">
          {SERVICENOW_METHODS.map(method=><button
            type="button"
            key={method.id}
            className={`jira-auth-method compact ${serviceNowMethod===method.id?'selected':''}`}
            onClick={()=>setForm({...form,serviceNowAuthMethod:method.id,authType:method.id,username:'',secret:''})}
          >
            <span className="jira-auth-radio" aria-hidden="true"/>
            <span className="jira-auth-compact-label">
              <b>{method.label}</b>
              <em>{method.hint}</em>
            </span>
          </button>)}
        </div>

        <div className="jira-auth-selected-help">
          {SERVICENOW_METHODS.find(method=>method.id===serviceNowMethod)?.description}
        </div>
      </div>}

      {isServiceNow&&serviceNowMethod==='oauth'&&<div className="form-grid two connection-credential-grid compact">
        <label>
          <span>Client ID</span>
          <input value={form.username} onChange={e=>setForm({...form,username:e.target.value})} placeholder="OAuth application Client ID"/>
        </label>
        <label>
          <span>Client Secret</span>
          <div className="connection-secret"><KeyRound size={15}/><input type="password" autoComplete="new-password" data-lpignore="true" value={form.secret} onChange={e=>setForm({...form,secret:e.target.value})} placeholder={editing!=='new'?'Leave blank to keep existing secret':''}/></div>
        </label>
      </div>}

      {isServiceNow&&serviceNowMethod==='oauth'&&<div className="oauth-connect-panel compact">
        <div>
          <b>{serviceNowOauthSaved?'ServiceNow OAuth configured':'Connect to ServiceNow'}</b>
          <span>{serviceNowOauthSaved?'Reconnect if access was revoked, expired or the OAuth application changed.':'The ServiceNow admin must register this callback URL: '+`${window.location.origin}/api/source-connections/oauth/servicenow/callback`}</span>
        </div>
        <button className="pa-primary" disabled={!!busy||!form.name.trim()||!form.baseUrl.trim()||!form.username.trim()||(editing==='new'&&!form.secret.trim())} onClick={connectServiceNow}>
          <Link2 size={15}/>{busy==='servicenow-oauth'?'Waiting…':serviceNowOauthSaved?'Reconnect':'Connect ServiceNow'}
        </button>
      </div>}

      {isServiceNow&&serviceNowMethod==='client_credentials'&&<div className="form-grid two connection-credential-grid compact">
        <label>
          <span>Client ID</span>
          <input value={form.username} onChange={e=>setForm({...form,username:e.target.value})} placeholder="OAuth application Client ID"/>
        </label>
        <label>
          <span>Client Secret</span>
          <div className="connection-secret"><KeyRound size={15}/><input type="password" autoComplete="new-password" data-lpignore="true" value={form.secret} onChange={e=>setForm({...form,secret:e.target.value})} placeholder={editing!=='new'?'Leave blank to keep existing secret':''}/></div>
        </label>
      </div>}

      {isServiceNow&&serviceNowMethod==='basic'&&<div className="form-grid two connection-credential-grid compact">
        <label>
          <span>Integration username</span>
          <input value={form.username} onChange={e=>setForm({...form,username:e.target.value})}/>
        </label>
        <label>
          <span>Password</span>
          <div className="connection-secret"><KeyRound size={15}/><input type="password" autoComplete="new-password" data-lpignore="true" value={form.secret} onChange={e=>setForm({...form,secret:e.target.value})} placeholder={editing!=='new'?'Leave blank to keep existing password':''}/></div>
        </label>
      </div>}

      {isServiceNow&&serviceNowMethod==='bearer'&&<div className="form-grid one connection-credential-grid compact">
        <label>
          <span>Bearer access token</span>
          <div className="connection-secret"><KeyRound size={15}/><input type="password" autoComplete="new-password" data-lpignore="true" value={form.secret} onChange={e=>setForm({...form,secret:e.target.value})} placeholder={editing!=='new'?'Leave blank to keep existing token':''}/></div>
        </label>
      </div>}

      {isJira&&jiraMethod!=='oauth'&&<div className="form-grid two connection-credential-grid compact">
        <label>
          <span>Email / account{jiraMethod==='scoped_api_token'?' (optional)':''}</span>
          <input autoComplete="off" value={form.username} onChange={e=>setForm({...form,username:e.target.value})}/>
        </label>
        <label>
          <span>{jiraMethod==='scoped_api_token'?'Scoped API token':'API token'}</span>
          <div className="connection-secret"><KeyRound size={15}/><input type="password" autoComplete="new-password" data-lpignore="true" value={form.secret} onChange={e=>setForm({...form,secret:e.target.value})} placeholder={editing!=='new'?'Leave blank to test saved credential, or enter a replacement token':''}/></div>
        </label>
      </div>}

      <div className="connection-editor-footer">
        <div>
          {editorTest.state!=='idle'&&<div className={`connection-test-result compact ${editorTest.state}`}>
            <b>{editorTest.state==='testing'?'Testing connection':editorTest.state==='success'?'Connection successful':'Connection failed'}</b>
            <span>{editorTest.message}</span>
          </div>}
        </div>
        <div className="settings-actions compact">
          {!(isJira&&jiraMethod==='oauth'&&editing==='new')&&!(isServiceNow&&serviceNowMethod==='oauth'&&editing==='new')&&<button className="pa-primary" disabled={!!busy||!form.name.trim()||!form.baseUrl.trim()} onClick={save}>{busy==='save'?'Saving…':'Save'}</button>}
          <button className="pa-secondary" disabled={!!busy||!form.baseUrl.trim()||((isJira&&jiraMethod!=='oauth')&&editing==='new'&&!form.secret.trim())||((isServiceNow&&serviceNowMethod!=='oauth')&&editing==='new'&&!form.secret.trim())} onClick={testForm}>
            {busy==='test-form'?'Testing…':isJira&&jiraMethod==='oauth'&&editing==='new'?'Connect & test':isServiceNow&&serviceNowMethod==='oauth'&&editing==='new'?'Connect & test':'Test'}
          </button>
          <button className="pa-secondary" disabled={!!busy && busy!=='oauth'} onClick={close}>Cancel</button>
        </div>
      </div>
    </div>}
  </div>;
}
